import { EventBus, Events } from "jiffy-store";
import { readCookie } from "common/utils/cookie";
import { isMobile } from "common/utils/browser";
import { isFlashPdp } from "components/jiffy/product-details/customizable-made-page-utils";
import { dispatchZipcodeUpdatedEvent } from "components/jiffy/masthead-redesign2025/local-delivery/utils/zipcode-helper";
import DeliverToModalSiteClass from "./classes/deliver-to-modal-site-class";
import DeliverToToggleDesktopClass from "./classes/deliver-to-toggle-desktop-class";
import DeliverToToggleMobileClass from "./classes/deliver-to-toggle-mobile-class";
import DeliverToZipCodeFormClass from "./classes/deliver-to-zip-code-form-class";
import DeliverToUserFormClass from "./classes/deliver-to-user-form-class";
import DeliverToConfirmationWindowClass from "./classes/deliver-to-confirmation-window-class";

class DeliverToSite {
  constructor() {
    this.initModal();
    this.initConfirmWindow();
    this.initZipCodeForm();
    this.initUserForm();
    this.initDeliverToToggle();

    this.initEvents();
  }

  static isPdpPage() {
    return document.querySelector(".js-product-details") !== null;
  }

  initModal() {
    this.deliverToModal = new DeliverToModalSiteClass({
      modalSelector: "#js-deliver-to-modal-site",
      addressSelector: ".js-deliver-to-mob-addr, .js-deliver-to-address",
      modalInputSelector: ".js-deliver-to-modal-input"
    });
  }

  initConfirmWindow() {
    this.confirmWindow = new DeliverToConfirmationWindowClass({
      chosenModalWindowSelector: ".js-deliver-to-chosen-wrapper",
      mainModalWindowSelector: ".js-deliver-to-form-wrapper",
      chosenZipcodeSelector: ".js-deliver-to-my-zipcode",
      chosenCitySelector: ".js-deliver-to-my-city",
      confirmButtonSelector: ".js-deliver-to-chosen-button"
    });
  }

  initZipCodeForm() {
    this.zipCodeForm = new DeliverToZipCodeFormClass({
      formSelector: ".js-deliver-to-submit-form",
      inputSelector: ".js-deliver-to-modal-input",
      loadingSelector: ".js-deliver-to-modal-loading",
      submitSelector: ".js-deliver-to-modal-submit",
      errorSelector: ".js-deliver-to-modal-form-error"
    });
  }

  initUserForm() {
    this.userForm = new DeliverToUserFormClass({
      formSelector: ".js-deliver-to-user-form",
      inputsSelector: ".js-deliver-to-user-input",
      addressesLinkSelector: ".js-deliver-to-addresses"
    });
  }

  initDeliverToToggle() {
    this.deliverToToggle = isMobile()
      ? new DeliverToToggleMobileClass(".js-deliver-to-toggle-modal", {
          chosenPdpAddressSelector: ".js-deliver-to-pdp-addr",
          chosenAddressSelector: ".js-deliver-to-mob-addr"
        })
      : new DeliverToToggleDesktopClass(".js-deliver-to-toggle-modal", {
          chosenPdpAddressSelector: ".js-deliver-to-pdp-addr",
          chosenAddressSelector: ".js-deliver-to-address",
          titleSelector: ".js-deliver-to-title",
          usernameSelector: ".js-deliver-to-username"
        });
  }

  initEvents() {
    this.deliverToToggle.onToggleCallback((context, $triggerElement) => {
      this.zipCodeForm.updateTrackAttributeContext(context);
      this.deliverToModal.updateTrackAttributeContext(context).openModal($triggerElement);
    });

    this.deliverToModal.fetchModalBody().then(() => {
      this.zipCodeForm.fetchDomElements();
      this.confirmWindow.fetchDomElements();
      this.userForm.fetchDomElements();
    });

    this.userForm.onSelectAddressCallback(({ zipcode, city, addressID, address, username }) => {
      this.userForm.setAddress(addressID).then(() => {
        this.deliverToToggle.fillAddress({ address, zipcode, username });
        this.confirmWindow.fillWindow({ zipcode, city }).showWindow();

        if (DeliverToSite.isPdpPage() && !isFlashPdp()) {
          this.zipCodeForm.updateLocation(zipcode).then(() => {
            dispatchZipcodeUpdatedEvent(zipcode);
          });
        } else {
          this.deliverToModal.reloadPageWithModal();
        }
      });
    });

    this.zipCodeForm.onSubmitCallback(({ zipcode, city, address }) => {
      this.userForm.unsetAddress().then(() => {
        this.deliverToToggle.fillAddress({ address, zipcode });
        this.confirmWindow.fillWindow({ zipcode, city }).showWindow();

        if (DeliverToSite.isPdpPage() && !isFlashPdp()) {
          dispatchZipcodeUpdatedEvent(zipcode);
        } else {
          this.deliverToModal.reloadPageWithModal();
        }
      });
    });

    this.deliverToModal.onCloseModalCallback(() => {
      this.zipCodeForm.clearForm();
      this.confirmWindow.hideWindow();
    });

    this.handleLocalDeliveryAddressUpdated();
  }

  handleLocalDeliveryAddressUpdated() {
    // Listen to LOCAL_DELIVERY_ADDRESS_UPDATED event from new local-delivery modal
    EventBus.on(Events.LOCAL_DELIVERY_ADDRESS_UPDATED, event => {
      const address = event.target;
      if (address) {
        const formattedAddress = [
          address.address1,
          [address.city, address.zipcode].filter(Boolean).join(" ")
        ]
          .filter(Boolean)
          .join(", ");

        this.deliverToToggle.fillAddress({
          address: formattedAddress,
          zipcode: address.zipcode,
          username: readCookie("deliver_to_name") || ""
        });
      }
    });
  }
}

// eslint-disable-next-line max-lines-per-function
export default () => new DeliverToSite();
